'use client'

import React, { useCallback, useEffect, useId, useRef, useState } from 'react'
import { ArrowLeft, Copy, QrCode, Share2 } from 'lucide-react'
import { WagamourV2Modal } from '@/components/v2/WagamourV2Modal'
import { WagamourV2Button } from '@/components/v2/WagamourV2Button'
import { WagamourV2ModalEyebrow } from '@/components/v2/WagamourV2ModalContent'
import {
  ConnectDogCardQrCode,
} from '@/components/play/ConnectDogCardQrCode'
import {
  copyConnectDogCardUrl,
  isWebShareSupported,
  shareUrlPayload,
} from '@/lib/play/connectShareAdapter'
import {
  buildWagamourInviteSharePayload,
  resolveWagamourInviteUrl,
} from '@/lib/play/wagamourInviteReferral'
import { v2ModalStyles } from '@/lib/design/v2ModalContent'
import { playV2FocusRing } from '@/lib/play/playV2Focus'

export type InviteDogParentShareSheetProps = {
  isOpen: boolean
  onClose: () => void
  /** Dev-only: explicit preview landing control */
  showPreviewLandingAction?: boolean
  portalContainer?: HTMLElement | null
  returnFocusRef?: React.RefObject<HTMLElement | null>
}

type SheetView = 'share' | 'qr'

const PREVIEW_LANDING_WINDOW_NAME = 'wagamour-invite-preview-landing'

function WagamourInviteQrInstruction({ id }: { id?: string }) {
  return (
    <p id={id} className="text-center text-sm leading-relaxed text-white/80">
      Ask them to scan this code to join Wagamour.
    </p>
  )
}

export function InviteDogParentShareSheet({
  isOpen,
  onClose,
  showPreviewLandingAction = false,
  portalContainer = null,
  returnFocusRef,
}: InviteDogParentShareSheetProps) {
  const titleId = useId()
  const statusId = useId()
  const [view, setView] = useState<SheetView>('share')
  const [actionBusy, setActionBusy] = useState(false)
  const [statusMessage, setStatusMessage] = useState<string | null>(null)
  const [manualCopyUrl, setManualCopyUrl] = useState<string | null>(null)
  const [previewFallbackUrl, setPreviewFallbackUrl] = useState<string | null>(null)
  const openedRef = useRef(false)

  const inviteUrl = resolveWagamourInviteUrl()

  const resetSheet = useCallback(() => {
    setView('share')
    setActionBusy(false)
    setStatusMessage(null)
    setManualCopyUrl(null)
    setPreviewFallbackUrl(null)
  }, [])

  const handleClose = useCallback(() => {
    resetSheet()
    onClose()
    window.requestAnimationFrame(() => {
      returnFocusRef?.current?.focus()
    })
  }, [onClose, resetSheet, returnFocusRef])

  useEffect(() => {
    if (isOpen) {
      openedRef.current = true
      return
    }
    if (openedRef.current) {
      resetSheet()
    }
  }, [isOpen, resetSheet])

  const handleShareWagamour = useCallback(async () => {
    if (actionBusy) return
    setActionBusy(true)
    setStatusMessage(null)
    setManualCopyUrl(null)
    try {
      const payload = buildWagamourInviteSharePayload()
      if (!isWebShareSupported()) {
        const copied = await copyConnectDogCardUrl(payload.url)
        if (copied.outcome === 'copied') {
          setStatusMessage('Wagamour invite link copied.')
        } else if (copied.outcome === 'manual_required') {
          setManualCopyUrl(copied.url)
          setStatusMessage('Copy the link below.')
        } else {
          setStatusMessage("We couldn't share the invite. Try Copy Invite Link.")
        }
        return
      }

      const result = await shareUrlPayload(payload)
      if (result.outcome === 'shared') {
        setStatusMessage('Invite shared.')
      } else if (result.outcome === 'cancelled') {
        setStatusMessage(null)
      } else if (result.outcome === 'unavailable') {
        const copied = await copyConnectDogCardUrl(payload.url)
        if (copied.outcome === 'copied') {
          setStatusMessage('Wagamour invite link copied.')
        } else if (copied.outcome === 'manual_required') {
          setManualCopyUrl(copied.url)
          setStatusMessage('Copy the link below.')
        } else {
          setStatusMessage("We couldn't share the invite. Try Copy Invite Link.")
        }
      } else {
        setStatusMessage(result.message)
      }
    } finally {
      setActionBusy(false)
    }
  }, [actionBusy])

  const handleCopyInviteLink = useCallback(async () => {
    if (actionBusy) return
    setActionBusy(true)
    setStatusMessage(null)
    try {
      const url = resolveWagamourInviteUrl()
      const result = await copyConnectDogCardUrl(url)
      if (result.outcome === 'copied') {
        setStatusMessage('Wagamour invite link copied.')
        setManualCopyUrl(null)
      } else if (result.outcome === 'manual_required') {
        setManualCopyUrl(result.url)
        setStatusMessage('Copy the link below.')
      } else {
        setStatusMessage("We couldn't copy the link. Please try again.")
      }
    } finally {
      setActionBusy(false)
    }
  }, [actionBusy])

  const handleShowQr = useCallback(() => {
    setView('qr')
    setStatusMessage(null)
    setManualCopyUrl(null)
  }, [])

  const handlePreviewLanding = useCallback(() => {
    const url = resolveWagamourInviteUrl()
    const popup = window.open(url, PREVIEW_LANDING_WINDOW_NAME, 'noopener,noreferrer')
    if (!popup) {
      setPreviewFallbackUrl(url)
      setStatusMessage('Popup blocked — use the link below to preview.')
      return
    }
    setPreviewFallbackUrl(null)
    setStatusMessage('Invite landing preview opened in a new tab.')
  }, [])

  return (
    <WagamourV2Modal
      isOpen={isOpen}
      onClose={handleClose}
      presentation="sheet"
      portalContainer={portalContainer}
      preventClose={actionBusy}
      ariaLabelledBy={titleId}
      header={
        <div className="space-y-1">
          {view === 'qr' ? (
            <button
              type="button"
              className={`mb-1 inline-flex min-h-[44px] items-center gap-1 text-sm text-white/75 hover:text-white ${playV2FocusRing}`}
              onClick={() => {
                setView('share')
                setStatusMessage(null)
              }}
            >
              <ArrowLeft className="h-4 w-4" aria-hidden />
              Back
            </button>
          ) : null}
          <WagamourV2ModalEyebrow>Invite</WagamourV2ModalEyebrow>
          <h2 id={titleId} className="text-base font-bold leading-snug" style={v2ModalStyles.headline}>
            {view === 'qr' ? 'Join Wagamour' : 'Invite a Dog Parent'}
          </h2>
        </div>
      }
      footer={
        view === 'share' ? (
          <div className="space-y-2">
            <WagamourV2Button
              type="button"
              fullWidth
              disabled={actionBusy}
              onClick={() => void handleShareWagamour()}
              data-invite-share-primary="true"
            >
              <span className="inline-flex items-center justify-center gap-2">
                <Share2 className="h-4 w-4" aria-hidden />
                Share Wagamour
              </span>
            </WagamourV2Button>
            <div className="grid grid-cols-2 gap-2">
              <WagamourV2Button
                type="button"
                fullWidth
                variant="secondary"
                disabled={actionBusy}
                onClick={handleShowQr}
                data-invite-share-qr="true"
              >
                <span className="inline-flex items-center justify-center gap-2">
                  <QrCode className="h-4 w-4" aria-hidden />
                  Show QR Code
                </span>
              </WagamourV2Button>
              <WagamourV2Button
                type="button"
                fullWidth
                variant="secondary"
                disabled={actionBusy}
                onClick={() => void handleCopyInviteLink()}
                data-invite-share-copy="true"
              >
                <span className="inline-flex items-center justify-center gap-2">
                  <Copy className="h-4 w-4" aria-hidden />
                  Copy Invite Link
                </span>
              </WagamourV2Button>
            </div>
            {showPreviewLandingAction ? (
              <button
                type="button"
                className={`mx-auto block min-h-[44px] w-full rounded-full px-3 text-center text-[11px] font-medium text-amber-200/90 underline [text-underline-offset:2px] ${playV2FocusRing}`}
                disabled={actionBusy}
                onClick={handlePreviewLanding}
                data-invite-share-preview-landing="true"
              >
                Preview invite landing page (dev only)
              </button>
            ) : null}
          </div>
        ) : undefined
      }
      contentClassName="pb-[max(0.5rem,env(safe-area-inset-bottom,0px))]"
    >
      <div
        className="space-y-4"
        aria-labelledby={titleId}
        data-invite-dog-parent-share-sheet="true"
        data-invite-share-view={view}
      >
        {view === 'share' ? (
          <>
            <p className="text-sm leading-relaxed" style={v2ModalStyles.body}>
              Invite someone you know to join Wagamour.
            </p>
            <p className="text-xs leading-relaxed text-white/65">
              They can create their dog&apos;s profile and start building their own Play Circle.
            </p>
            <p className="text-xs leading-relaxed text-white/55">
              This invite does not connect dogs automatically — use Connect our Dogs for that.
            </p>
          </>
        ) : (
          <div
            className="space-y-4 py-2"
            data-invite-qr-panel="true"
            data-invite-qr-encoded-url={inviteUrl}
          >
            <WagamourInviteQrInstruction id={`${titleId}-qr-instruction`} />
            <ConnectDogCardQrCode url={inviteUrl} size={220} />
          </div>
        )}

        <p
          id={statusId}
          className="min-h-[1.25rem] text-center text-xs text-emerald-200/90"
          role="status"
          aria-live="polite"
          data-invite-share-status="true"
        >
          {statusMessage}
        </p>

        {manualCopyUrl ? (
          <div className="rounded-xl border border-white/15 bg-black/30 p-3">
            <p className="mb-2 text-xs text-white/70">Select and copy this link:</p>
            <input
              type="text"
              readOnly
              value={manualCopyUrl}
              className="w-full rounded-lg border border-white/10 bg-black/40 px-2 py-2 text-xs text-white"
              onFocus={(e) => e.currentTarget.select()}
              aria-label="Wagamour invite link for manual copy"
              data-invite-share-manual-copy="true"
            />
          </div>
        ) : null}

        {previewFallbackUrl ? (
          <a
            href={previewFallbackUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={`block text-center text-xs font-medium text-amber-100 underline [text-underline-offset:2px] ${playV2FocusRing}`}
            data-invite-share-preview-fallback="true"
          >
            Open invite landing preview
          </a>
        ) : null}
      </div>
    </WagamourV2Modal>
  )
}
